import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Landmark } from 'lucide-react';
import {
  fetchSiteElectricity, fetchSiteTimeZones, formatCents, getDisplayErrorMessage,
  type SiteElectricityRow, type SiteTimeZoneRow,
} from '@/lib/api';
import { downloadAuthenticatedCsv } from '@/lib/downloadAuthenticatedCsv';
import { formatCalendarDay } from '@/lib/bankWindowCopy';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './SiteElectricity.module.css';

/**
 * Site Electricity (Super Admin; Site Host Terms pre-flip, 2026-10-02).
 *   1. Manual reimbursement: locations launched on or after 2 October 2026
 *      that their launch did not enroll (the terms were not published in the
 *      Platform yet, or the launch was outside commissioning). The ledger
 *      does not track them until the publication bank-window catch-up is
 *      applied from the original launch. The report supports manual review
 *      while that application is pending.
 *   2. Transition Notices: locations under an existing agreement, and the
 *      Transition Dates recorded for them (recorded on each location's page).
 *   3. Time zones: locations without a usable zone, time-of-use first. Each
 *      environment's page reports that environment.
 * The server routes are Super Admin only; a hand-typed URL shows an error.
 */

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'None');
const SOURCE = { commissioning: 'commissioning', session: 'first session', charger: 'charger registered' } as const;
const CATEGORY = {
  window_launch: 'Launched while unpublished',
  unenrolled_after_publication: 'Not enrolled at launch',
  transition_before_tracking: 'Transition Date before it was recorded',
} as const;
const ZONE_STATE = { missing: 'Not set', invalid: 'Not a time zone', not_site_zone: 'Fixed offset or UTC' } as const;

function locationLink(r: { location_id: string; location_name?: string; name?: string }) {
  return <a href={`/locations/${r.location_id}`}>{r.location_name ?? r.name}</a>;
}

function totalsCell(r: SiteElectricityRow) {
  if (r.totals.length === 0) return 'No sessions';
  return <>{r.totals.map(t => <span key={t.currency ?? 'none'} className={styles.small}>
    {t.sessions} sessions, {t.kwh} kWh, {t.currency ? formatCents(t.estimated_amount_cents, t.currency) : 'currency unknown'}
  </span>)}</>;
}

function exceptionsCell(r: SiteElectricityRow) {
  const parts: string[] = [];
  for (const t of r.totals) {
    if (t.per_hour_sessions) parts.push(`${t.per_hour_sessions} per hour`);
    if (t.unpriced_sessions) parts.push(`${t.unpriced_sessions} unpriced`);
    if (t.tou_without_timezone_sessions) parts.push(`${t.tou_without_timezone_sessions} time-of-use without a time zone`);
    if (t.pin_estimated_sessions) parts.push(`${t.pin_estimated_sessions} priced from the settlement rate`);
    if (t.settled_with_session_sessions) parts.push(`${t.settled_with_session_sessions} already paid with the session, left out`);
  }
  if (r.mirrored_sessions_excluded) parts.push(`${r.mirrored_sessions_excluded} v1 sessions left out`);
  if (r.sessions_truncated) parts.push('session count capped');
  if (r.tenant_is_per_session_payee) parts.push('operator is paid per session: check for double payment');
  return parts.length ? parts.join('; ') : 'None';
}

const manualColumns: Column<SiteElectricityRow>[] = [
  { key: 'location', header: 'Location', mobile: 'primary', cell: locationLink },
  { key: 'operator', header: 'Operator', mobile: 'secondary', cell: r => r.tenant_name ?? r.tenant_id },
  { key: 'category', header: 'Why', cell: r => r.category ? CATEGORY[r.category] : '' },
  { key: 'launch', header: 'First launch', cell: r => <>{day(r.first_launch_at)}<span className={styles.small}>{r.first_launch_source ? SOURCE[r.first_launch_source] : ''}</span></> },
  { key: 'period', header: 'Period', cell: r => `From ${day(r.period_start)} to ${r.transition_on ? `${formatCalendarDay(r.transition_on)} (Transition Date)` : day(r.period_end)}` },
  { key: 'usage', header: 'Sessions and estimate', cell: totalsCell },
  { key: 'exceptions', header: 'Check', cell: exceptionsCell },
  { key: 'bank', header: 'Bank', cell: r => r.bank_first_verified_at ? `Verified ${day(r.bank_first_verified_at)}` : r.bank_account_connected ? 'Connected, not verified' : 'Not connected' },
  { key: 'terms', header: 'Terms accepted', cell: r => r.site_host_terms_accepted_at ? day(r.site_host_terms_accepted_at) : 'No' },
];

const transitionColumns: Column<SiteElectricityRow>[] = [
  { key: 'location', header: 'Location', mobile: 'primary', cell: locationLink },
  { key: 'operator', header: 'Operator', mobile: 'secondary', cell: r => r.tenant_name ?? r.tenant_id },
  { key: 'launch', header: 'First launch', cell: r => r.legacy_v1 && !r.first_launch_at ? 'v1 site' : <>{day(r.first_launch_at)}{r.legacy_v1 ? <span className={styles.small}>v1 site</span> : null}</> },
  { key: 'status', header: 'Transition', cell: r => r.enrollment === 'transition_scheduled' && r.transition_on
    ? `Transition Date ${formatCalendarDay(r.transition_on)}` : 'No notice recorded' },
  { key: 'rate', header: 'Rate now', cell: r => r.applicable ? r.rate_mode_now.replaceAll('_', ' ') : 'No reimbursement rate' },
  { key: 'bank', header: 'Bank', cell: r => r.bank_first_verified_at ? `Verified ${day(r.bank_first_verified_at)}` : r.bank_account_connected ? 'Connected, not verified' : 'Not connected' },
  { key: 'terms', header: 'Terms accepted', cell: r => r.site_host_terms_accepted_at ? day(r.site_host_terms_accepted_at) : 'No' },
];

const zoneColumns: Column<SiteTimeZoneRow>[] = [
  { key: 'location', header: 'Location', mobile: 'primary', cell: locationLink },
  { key: 'state', header: 'Time zone', cell: r => `${ZONE_STATE[r.zone_state]}${r.timezone ? ` (${r.timezone})` : ''}` },
  { key: 'tou', header: 'Time-of-use rate', cell: r => (r.time_of_use ? 'Yes: reimbursement goes to review until set' : 'No') },
  { key: 'suggested', header: 'From the address', cell: r => r.suggested_zone ?? 'Ambiguous: choose by hand' },
  { key: 'type', header: 'Type', mobile: 'hidden', cell: r => r.type ?? '' },
];

export function SiteElectricityPage() {
  useRecentRoute('Site Electricity');
  const report = useQuery({ queryKey: ['site-electricity'], queryFn: fetchSiteElectricity, staleTime: 60_000 });
  const zones = useQuery({ queryKey: ['site-electricity', 'time-zones'], queryFn: fetchSiteTimeZones, staleTime: 60_000 });
  const [csvError, setCsvError] = useState('');
  const [exporting, setExporting] = useState(false);
  const rows = report.data?.rows ?? [];
  const manual = rows.filter(r => r.category !== null);
  const transitions = rows.filter(r => r.enrollment === 'existing_agreement' || r.enrollment === 'transition_scheduled');
  const terms = report.data?.site_host_terms;

  async function exportCsv() {
    setExporting(true); setCsvError('');
    try {
      await downloadAuthenticatedCsv({ path: 'admin/site-electricity/locations', query: { format: 'csv' },
        fallbackFilename: `site-electricity-${new Date().toISOString().slice(0, 10)}.csv` });
    } catch (err) { setCsvError(await getDisplayErrorMessage(err, 'Could not export the CSV.')); }
    finally { setExporting(false); }
  }

  return <div className={styles.page}>
    <header className={styles.header}>
      <span className={styles.headerIcon}><Landmark size={20} aria-hidden /></span>
      <div>
        <h1 className={styles.title}>Site Electricity</h1>
        <p className={styles.subtitle}>Site Host Terms: which locations the ledger tracks, which are reimbursed by hand, and Transition Notices.</p>
      </div>
    </header>
    {report.isError && <p className={styles.notice} role="alert">Site Electricity is unavailable. It is for Super Admins only.</p>}
    {terms && <p className={styles.notice}>{terms.published
      ? `Site Host Terms ${terms.version} published in the Platform on ${day(terms.published_at)}. Locations launched by commissioning from then are enrolled at launch.`
      : 'The Site Host Terms are not published in the Platform. Nothing enrolls and the ledger records nothing until they are.'}</p>}

    <section className={styles.section} aria-labelledby="manual-heading">
      <div className={styles.sectionHead}>
        <h2 id="manual-heading" className={styles.sectionTitle}>Manual reimbursement ({manual.length})</h2>
        <button type="button" className={formStyles.btnSecondary} disabled={exporting} onClick={() => void exportCsv()}>{exporting ? 'Exporting...' : 'Export CSV'}</button>
      </div>
      <p className={styles.lead}>Locations that launched on or after 2 October 2026 without enrolling (the Site Host Terms were not published in the Platform yet, or the launch was outside commissioning), and Transition Dates recorded after they passed. The ledger does not track these periods. Reimburse them by hand under the Site Host Terms for the period shown. The estimate uses the ledger's rule: energy times the rate when the session started, per kWh only, leaving out sessions whose electricity was already paid with the session.</p>
      {csvError && <p role="alert" className={styles.notice}>{csvError}</p>}
      {report.isPending ? <p className={styles.muted}>Loading...</p>
        : <ResponsiveTable columns={manualColumns} rows={manual} rowKey={r => r.location_id} emptyMessage="No location needs manual reimbursement." />}
    </section>

    <section className={styles.section} aria-labelledby="transition-heading">
      <h2 id="transition-heading" className={styles.sectionTitle}>Transition Notices ({transitions.length})</h2>
      <p className={styles.lead}>Locations launched before 2 October 2026 stay under their existing agreement and show no bank countdown until a Transition Notice is recorded. Record one on the location's page: the countdown and the ledger start on its Transition Date.</p>
      {report.isPending ? <p className={styles.muted}>Loading...</p>
        : <ResponsiveTable columns={transitionColumns} rows={transitions} rowKey={r => r.location_id} emptyMessage="No location is waiting for a Transition Notice." />}
    </section>

    <section className={styles.section} aria-labelledby="zone-heading">
      <h2 id="zone-heading" className={styles.sectionTitle}>Time zones ({zones.data?.without_zone ?? 0} not set, {zones.data?.time_of_use_without_zone ?? 0} with a time-of-use rate)</h2>
      <p className={styles.lead}>Time-of-use periods are read in each site's local time, and a time-of-use rate cannot be saved without a time zone. Set one on the location's page.</p>
      {zones.isPending ? <p className={styles.muted}>Loading...</p> : zones.isError ? <p className={styles.notice}>Time zones are unavailable.</p>
        : <ResponsiveTable columns={zoneColumns} rows={zones.data?.rows ?? []} rowKey={r => r.location_id} emptyMessage="Every location has a time zone." />}
    </section>
    {(report.data?.truncated || zones.data?.truncated) && <p className={styles.muted}>Only the first 2,000 locations are shown.</p>}
  </div>;
}
